import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { Link } from 'react-router-dom';

interface RPillButtonProps {
    children: React.ReactNode;
    onClick?: () => void;
    to?: string;
    variant?: 'primary' | 'secondary';
    icon?: React.ReactNode;
    disabled?: boolean;
    color?: string;
    hoverColor?: string;
}

export function RPillButton({ children, onClick, to, variant = 'primary', icon, disabled, color }: RPillButtonProps) {
    const isPrimary = variant === 'primary';

    const colorStyle = color ? (isPrimary ? { backgroundColor: color } : { borderColor: color, color }) : undefined;

    if (to && !disabled) {
        const LinkComponent = isPrimary ? PrimaryPillLink : SecondaryPillLink;
        return (
            <LinkComponent to={to} style={colorStyle}>
                {icon}
                {children}
            </LinkComponent>
        );
    }

    const Button = isPrimary ? PrimaryPill : SecondaryPill;
    return (
        <Button onClick={onClick} disabled={disabled} style={colorStyle}>
            {icon}
            {children}
        </Button>
    );
}

const basePill = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '8px 24px',
    borderRadius: 24,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    textDecoration: 'none',
    '&:disabled': {
        opacity: 0.6,
        cursor: 'not-allowed',
    },
    outline: 'none',
    '&:focus, &:focus-visible': {
        outline: 'none',
        boxShadow: 'none',
    },
};

const PrimaryPill = styled('button')({
    ...basePill,
    border: 'none',
    backgroundColor: Colors.darkNavy,
    color: Colors.white,
    '&:hover:not(:disabled)': {
        backgroundColor: Colors.pressedNavy,
        color: Colors.white,
    },
});

const SecondaryPill = styled('button')({
    ...basePill,
    border: `1px solid ${Colors.lightGrey}`,
    backgroundColor: Colors.white,
    color: Colors.darkNavy,
    '&:hover:not(:disabled)': {
        backgroundColor: Colors.lightGrey,
    },
});

const PrimaryPillLink = styled(Link)({
    ...basePill,
    border: 'none',
    backgroundColor: Colors.darkNavy,
    color: Colors.white,
    '&:hover': {
        backgroundColor: Colors.pressedNavy,
        color: Colors.white,
        textDecoration: 'none',
    },
});

const SecondaryPillLink = styled(Link)({
    ...basePill,
    border: `1px solid ${Colors.lightGrey}`,
    backgroundColor: Colors.white,
    color: Colors.darkNavy,
    '&:hover': {
        backgroundColor: Colors.lightGrey,
        textDecoration: 'none',
    },
});
